import React, { useRef, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer } from 'antd';
import styled from '@emotion/styled';
import { CloseOutlined } from 'antd/icons';

interface MoreMenuProps {
  open: boolean;
  onClose: () => void;
  options: {
    name: string;
    action: () => void;
    active: boolean;
    testid: string;
    Icon: any;
    restricted?: boolean;
  }[];
}

interface CustomMenuButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  name: string;
  testid: string;
  active: boolean;
  Icon: React.ElementType;
}

const StyledButton = styled.button<{ active: boolean }>`
  width: 100%;
  border: 0;
  height: 20%;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
  padding: 4px 16px;
  font-size: 18px;
  background-color: ${({ active }) => (active ? '#ffffff' : 'var(--productPrimary)')};
  color: ${({ active }) => (active ? 'var(--active-nav-item-color)' : 'var(--primary-btn-font-color)')};

  outline: none;

  &:focus-visible {
    outline: 2px solid #3b99fc;
    outline-offset: 1px;
  }

  ${({ active }) =>
    !active &&
    `
    body[data-contrast='high'] & {
      color: #fff;
    }
  `}
`;

const CustomMenuButton = forwardRef<HTMLButtonElement, CustomMenuButtonProps>(
  ({ name, onClick, active, Icon, testid, ...rest }, ref) => (
    <StyledButton ref={ref} onClick={onClick} active={active} data-testid={testid} {...rest}>
      <Icon aria-hidden />
      <span style={{ marginLeft: '16px' }}>{name}</span>
    </StyledButton>
  )
);

const StyledCloseDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

export const MoreMenu = ({ open, onClose, options }: MoreMenuProps) => {
  const { t } = useTranslation('common');
  const firstMenuItemRef = useRef<HTMLButtonElement>(null);

  const handleAfterOpenChange = (open: boolean) => {
    if (open && firstMenuItemRef.current) setTimeout(() => firstMenuItemRef.current?.focus(), 200);
  };

  return (
    <Drawer
      placement="bottom"
      open={open}
      onClose={onClose}
      title="More"
      closable={false}
      extra={<CloseOutlined onClick={onClose} style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
      afterOpenChange={handleAfterOpenChange}
    >
      {options.map(({ action, ...options }, index) => (
        <CustomMenuButton
          {...options}
          key={options.testid}
          ref={index === 0 ? firstMenuItemRef : null}
          onClick={() => {
            action();
            onClose();
          }}
          data-testid={options.testid}
        />
      ))}
      <StyledCloseDiv>
        <Button onClick={onClose} data-testid="close-more-menu-button">
          {t('Close')}
        </Button>
      </StyledCloseDiv>
    </Drawer>
  );
};
